/* Vehicle steps, pure logic: resolved vehicle + the shopper's picks -> a plain view model (what to draw), the Buy
 * button text and the URL params. No DOM here, so the rules the shopper sees and the ones that unlock the button
 * never disagree. Needs window.SeatFit (core + rules); loaded before seatfit-vehicle-steps.js. */
(function () {
  'use strict';
  if (window.SeatFitStepsModel) return;

  // Rear is not a main card: the reference layout offers it through "Need Rear Seats Only?" next to Full Set.
  var MAIN_SETS = ['front', 'full', 'third'];
  var ROW_NAMES = { front: 'Front', rear: 'Rear', third: 'Third Row' };

  function slug(v) { return window.SeatFit ? window.SeatFit.slugify(v) : String(v == null ? '' : v).toLowerCase(); }

  // Settings accept both {make} and [make]; unknown tokens stay visible so a typo is noticed.
  function fill(tpl, vars) {
    return String(tpl == null ? '' : tpl).replace(/[[{]([a-z]+)[\]}]/gi, function (m, k) {
      k = k.toLowerCase();
      return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m;
    }).replace(/\s+/g, ' ').trim();
  }

  // Textarea "Label | link" lines -> [{label, url}] (breadcrumbs keep order and allow blank links).
  function parseLines(text) {
    return String(text || '').split(/\r?\n/).map(function (line) {
      var i = line.indexOf('|');
      var label = (i < 0 ? line : line.slice(0, i)).trim();
      return label ? { label: label, url: i < 0 ? '' : line.slice(i + 1).trim() } : null;
    }).filter(Boolean);
  }

  // Option pictures keyed by option slug; only http(s) or protocol-relative links are accepted.
  function parseImages(text) {
    var out = {};
    parseLines(text).forEach(function (it) {
      if (/^(https?:)?\/\//i.test(it.url)) out[slug(it.label)] = it.url;
    });
    return out;
  }

  function expandYears(ranges) {
    var seen = {}, list = [];
    (ranges || []).forEach(function (r) {
      for (var y = r[1]; y >= r[0]; y--) if (!seen[y]) { seen[y] = true; list.push(y); }
    });
    return list.sort(function (a, b) { return b - a; });
  }

  // Years the model is sold in, newest first (the reference Year list order). The index already has them,
  // so a changed year never waits for the make file. Falls back to ctx.years when core provides it.
  function years(ctx) {
    if (ctx && ctx.years && ctx.years.length) {
      return Promise.resolve(expandYears(ctx.years.map(function (y) { return Array.isArray(y) ? y : [y, y]; })));
    }
    if (!ctx || !ctx.make || !ctx.model) return Promise.resolve([]);
    return window.SeatFit.loadIndex(ctx.kind || 'seat').then(function (idx) {
      var mk = window.SeatFit.findBy(idx && idx.makes, ctx.make.s);
      var mi = mk && window.SeatFit.findBy(mk.models, ctx.model.s);
      return expandYears(mi && mi.y);
    }).catch(function () { return ctx.year ? [ctx.year] : []; });
  }

  function options(values, chosen, images) {
    return values.map(function (v) {
      return { label: v, value: slug(v), img: images[slug(v)] || '', selected: chosen === v };
    });
  }

  // Link text we could not match (make=foo, model=silverado-1500) read like a name next to the resolved
  // one ("2020 Chevrolet Silverado 1500", not "silverado-1500"). Shown with textContent only, kept short.
  function human(v) {
    return String(v || '').replace(/[-_+]+/g, ' ').replace(/\s+/g, ' ').trim()
      .replace(/(^| )(\S)/g, function (m, sp, c) { return sp + c.toUpperCase(); });
  }
  function vehicleText(ctx, raw) {
    var y = (ctx && ctx.year) || raw.year || '';
    var mk = ctx && ctx.make ? ctx.make.n : human(raw.make);
    var md = ctx && ctx.model ? ctx.model.n : human(raw.model);
    return [y, mk, md].filter(Boolean).join(' ').slice(0, 80) || 'your vehicle';
  }

  // Picker prefill: only values we recognised; a year outside every generation is left for the shopper.
  function prefill(ctx, raw) {
    var y = ctx && ctx.reason === 'year-out-of-range' ? '' : (ctx && ctx.year) || (raw && raw.year) || '';
    return { year: y, make: ctx && ctx.make ? ctx.make.s : '', model: ctx && ctx.model ? ctx.model.s : '' };
  }

  // st = {ctx, sel, s, years, images, open, revealRear, raw, loading}
  function build(st) {
    var ctx = st.ctx, s = st.s;
    if (st.loading || !ctx) return { kind: 'loading' };
    if (ctx.status === 'unsupported') {
      return { kind: 'message', tone: 'unsupported', text: fill(s.unsupported_text, { vehicle: vehicleText(ctx, st.raw) }),
        button: { label: s.unsupported_button, picker: prefill(ctx, st.raw) } };
    }
    if (ctx.status !== 'ok' && ctx.reason === 'load-failed') {
      return { kind: 'message', tone: 'failed', text: s.load_failed_text, button: { label: s.retry_label, retry: true } };
    }
    if (ctx.status !== 'ok' && ctx.reason !== 'no-year') {
      return { kind: 'message', tone: 'none', text: s.no_vehicle_text,
        button: { label: s.no_vehicle_button, picker: prefill(ctx, st.raw) } };
    }
    var vm = { kind: 'steps', steps: [], gen: null, set: null, missing: null,
      year: { num: 1, key: 'year', name: s.year_label, value: ctx.status === 'ok' ? ctx.year : null, years: st.years || [] } };
    var sel = st.sel;
    if (!sel) {
      vm.missing = { key: 'year', num: 1, name: s.year_label };
      return vm;
    }
    var num = 1;
    sel.steps.forEach(function (step) {
      num++;
      vm.steps.push({ num: num, key: step.key, name: step.name, value: step.value, auto: step.auto,
        open: isOpen(st.open, step.key, !step.auto), options: options(step.options, step.value, st.images) });
    });
    if (sel.needGen) {
      num++;
      var picked = sel.row ? sel.genOptions.filter(function (g) { return g.k === sel.row.k; })[0] : null;
      var hints = window.SeatFitGenHints ? window.SeatFitGenHints.forOptions(sel.genOptions) : [];
      vm.gen = { num: num, key: 'gen', name: s.generation_label, value: picked ? picked.label : null,
        open: isOpen(st.open, 'gen', true), options: sel.genOptions.map(function (g, i) {
          return { label: g.label, hint: hints[i] || '', value: g.value, img: '', selected: !!picked && picked.k === g.k };
        }) };
    }
    if (sel.row && !ctx.kind) vm.set = seatSet(st, sel, ++num); // covers and mats have no Seat Set step
    vm.missing = missing(sel.missing, vm, s);
    return vm;
  }

  function isOpen(open, key, dflt) { return open && key in open ? !!open[key] : dflt; }

  function seatSet(st, sel, num) {
    var s = st.s;
    var sets = sel.sets;
    var cards = MAIN_SETS.filter(function (x) { return sets.indexOf(x) >= 0; });
    var hasRear = sets.indexOf('rear') >= 0;
    // Three-row vehicles have no Full Set, so the 2nd row is a main card between Front and 3rd Row: behind the
    // "Need Rear Seats Only?" link it read as "rear only" to shoppers covering the whole car. Same price, same variant.
    var at = cards.indexOf('third');
    if (hasRear && at >= 0) cards.splice(at, 0, 'rear');
    // Once the shopper asked for rear only, the Rear card stays in the row (as in the reference layout).
    else if (hasRear && (st.revealRear || sel.set === 'rear' || !cards.length)) cards.push('rear');
    return {
      num: num, key: 'set', name: s.seat_set_label, value: sel.set ? s[sel.set + '_desc'] : null,
      open: isOpen(st.open, 'set', true), rearLink: hasRear && cards.indexOf('rear') < 0,
      cards: cards.map(function (x) {
        var label = s[x + '_label'] || x;
        return { label: label, value: x, selected: sel.set === x, badge: x === 'full' ? s.full_badge : '',
          img: s[x + '_image'] || st.images[slug(label)] || st.images[slug(window.SeatFit.SET_VALUES[x])] || '' };
      }),
      subs: !sel.set ? [] : sel.subtypes.map(function (sub, j) {
        return { idx: j, row: sub.row, title: (ROW_NAMES[sub.row] || sub.row) + ' ' + s.seat_type_label,
          value: sub.value, options: options(sub.options, sub.value, {}) };
      })
    };
  }

  // Which step blocks the Buy button, numbered like the rows on screen.
  function missing(m, vm, s) {
    if (!m) return null;
    if (m.kind === 'step') {
      var st = vm.steps.filter(function (x) { return x.key === m.key; })[0];
      return { key: m.key, num: st ? st.num : 2, name: m.name };
    }
    if (m.kind === 'gen') return { key: 'gen', num: vm.gen ? vm.gen.num : 2, name: s.generation_label };
    var last = vm.gen || vm.steps[vm.steps.length - 1] || vm.year;
    if (m.kind === 'color') return { key: 'color', num: last.num + 1, name: s.color_option || m.name };
    var setNum = vm.set ? vm.set.num : 2;
    if (m.kind === 'set') return { key: 'set', num: setNum, name: s.seat_set_label };
    if (m.kind === 'seat') {
      return { key: 'seat-' + m.row, num: setNum + 1, name: (ROW_NAMES[m.row] || m.row) + ' ' + s.seat_type_label };
    }
    return { key: 'vehicle', num: 1, name: s.year_label };
  }

  // null = leave the label alone (still loading); '' never returned.
  function buttonLabel(st, vm) {
    var s = st.s;
    if (!vm || vm.kind === 'loading') return null;
    if (vm.kind === 'message') return s.atc_vehicle_label;
    if (vm.missing) return fill(s.atc_step_label, { n: vm.missing.num, step: vm.missing.name });
    return null;
  }

  function completeLabel(s, day) {
    return day ? fill(s.atc_complete_label, { day: String(day).toUpperCase() }) : s.atc_fallback_label;
  }

  // Params this block owns: only values the rules accepted (a stale or mistyped param never survives),
  // unreached steps removed. An open seat row keeps its empty slot so no answer shifts rows on reload.
  function urlParams(ctx, sel) {
    if (!ctx || ctx.status !== 'ok' || !sel) return null;
    // Key order = order new params are appended to the link: steps, then gen, set, seat (as seat links always read).
    var p = { year: ctx.year, make: ctx.make.s, model: ctx.model.s };
    (window.SeatFit.STEP_PARAMS || ['body', 's2', 's3']).forEach(function (k) { p[k] = null; });
    Object.assign(p, { gen: null, set: sel.set || null, seat: null });
    sel.steps.forEach(function (step) { if (step.value) p[step.key] = step.value; });
    if (sel.needGen && sel.row) {
      var g = sel.genOptions.filter(function (o) { return o.k === sel.row.k; })[0];
      p.gen = g ? g.value : null;
    }
    var seats = (sel.subtypes || []).map(function (x) { return x.value || ''; });
    if (sel.set && seats.some(Boolean)) p.seat = seats;
    return p;
  }

  window.SeatFitStepsModel = { fill: fill, parseLines: parseLines, parseImages: parseImages, years: years, build: build,
    buttonLabel: buttonLabel, completeLabel: completeLabel, urlParams: urlParams, vehicleText: vehicleText, prefill: prefill };
})();
